"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseAltiumAnnotationFile = parseAltiumAnnotationFile;
exports.validateAltiumAnnotationFile = validateAltiumAnnotationFile;
const zod_1 = require("zod");
const altiumValidation_1 = require("../../altiumValidation");
const altiumFileTypes_1 = require("../../altiumFileTypes");
const altiumBlockParser_1 = require("../common/altiumBlockParser");
const AltiumAnnotationKeyRegex = /^([a-zA-Z]+)(\d+)/;
const recordIndexSchema = zod_1.default.union([zod_1.default.number(), zod_1.default.literal(Infinity)]);
const IAltiumAnnotationSchema = zod_1.default.object({
    LogicalDesignator: zod_1.default.string(),
    LogicalPartID: zod_1.default.string(),
    DocumentName: zod_1.default.string(),
    ChannelName: zod_1.default.string(),
    UniqueID: zod_1.default.string(),
    PhysicalDesignator: zod_1.default.string(),
    PhysicalDesignatorLocked: zod_1.default.string(),
    SchematicPartUniqueId: zod_1.default.string(),
    _Index: recordIndexSchema,
    MultiPartId: zod_1.default.boolean().optional(),
});
const IAltiumSheetNumberManagerRecordSchema = zod_1.default.object({
    SheetNumberOrder: zod_1.default.string(),
    SheetNumberMethod: zod_1.default.string(),
    SheetNumbers: zod_1.default.array(zod_1.default.object({
        DocumentName: zod_1.default.string(),
        UniqueIDPath: zod_1.default.string(),
        SheetNumber: zod_1.default.number().int().positive(),
        _Index: zod_1.default.number().int().nonnegative(),
    })),
    Diagnostics: zod_1.default.array(zod_1.default.string()),
    InvalidSheetNumbers: zod_1.default
        .array(zod_1.default.object({ DocumentName: zod_1.default.string().optional(), UniqueIDPath: zod_1.default.string().optional(), _Index: recordIndexSchema }))
        .optional(),
});
const IAltiumSheetNumberRecordSchema = zod_1.default.object({
    DocumentName: zod_1.default
        .string()
        .min(1)
        .refine((value) => value === value.trim()),
    UniqueIDPath: zod_1.default.string().refine((value) => value === value.trim()),
    SheetNumber: zod_1.default.number().int().positive(),
    _Index: zod_1.default.number().int().nonnegative(),
});
function parseSheetNumberManager(blockContents, duplicateKeys) {
    const records = new Map();
    const indexedKeyRegex = /^(DocumentName|UniqueIDPath|SheetNumber)(\d+)$/;
    const duplicateIndexes = new Set(duplicateKeys.flatMap((key) => {
        const match = indexedKeyRegex.exec(key);
        return match === null ? [] : [Number(match[2])];
    }));
    for (const [key, value] of Object.entries(blockContents)) {
        const match = indexedKeyRegex.exec(key);
        if (match === null) {
            continue;
        }
        const fieldName = match[1];
        const index = Number(match[2]);
        const record = records.get(index) ?? { _Index: index };
        records.set(index, record);
        switch (fieldName) {
            case 'DocumentName':
                record.DocumentName = String(value);
                break;
            case 'UniqueIDPath':
                record.UniqueIDPath = String(value);
                break;
            case 'SheetNumber': {
                const sheetNumber = /^\d+$/.test(String(value)) ? Number(value) : undefined;
                record.SheetNumber = sheetNumber !== undefined && Number.isSafeInteger(sheetNumber) ? sheetNumber : undefined;
                break;
            }
        }
    }
    const sheetNumbers = [];
    const invalidSheetNumbers = [];
    const diagnostics = duplicateKeys.map((key) => `SheetNumberManager repeats field ${key}`);
    for (const [index, record] of records) {
        const parsedRecord = IAltiumSheetNumberRecordSchema.safeParse(record);
        if (duplicateIndexes.has(index) || !parsedRecord.success) {
            invalidSheetNumbers.push({
                _Index: index,
                DocumentName: duplicateKeys.includes(`DocumentName${index}`) ? undefined : record.DocumentName,
                UniqueIDPath: duplicateKeys.includes(`UniqueIDPath${index}`) ? undefined : record.UniqueIDPath,
            });
            if (!duplicateIndexes.has(index)) {
                diagnostics.push(`SheetNumberManager entry ${index} is incomplete or invalid`);
            }
            continue;
        }
        sheetNumbers.push(parsedRecord.data);
    }
    return IAltiumSheetNumberManagerRecordSchema.parse({
        SheetNumberOrder: String(blockContents.SheetNumberOrder ?? ''),
        SheetNumberMethod: String(blockContents.SheetNumberMethod ?? ''),
        SheetNumbers: sheetNumbers,
        ...(invalidSheetNumbers.length > 0 ? { InvalidSheetNumbers: invalidSheetNumbers } : {}),
        Diagnostics: diagnostics,
    });
}
function parseAnnotationsFromBlock(blockContents) {
    const annotationMap = new Map();
    for (const [key, value] of Object.entries(blockContents)) {
        const match = AltiumAnnotationKeyRegex.exec(key);
        if (!match) {
            continue;
        }
        const [_, fieldName, indexStr] = match;
        const index = parseInt(indexStr, 10);
        if (!annotationMap.has(index)) {
            annotationMap.set(index, { _Index: index });
        }
        const annotation = annotationMap.get(index);
        annotation[fieldName] = String(value);
    }
    const annotations = [];
    for (const [index, annotation] of annotationMap) {
        if (annotation.UniqueID) {
            annotation.SchematicPartUniqueId = annotation.UniqueID.substring(annotation.UniqueID.lastIndexOf('\\') + 1);
        }
        IAltiumAnnotationSchema.parse(annotation);
        annotations[index] = annotation;
    }
    return annotations;
}
function parseAltiumAnnotationFile(contents) {
    const blocks = (0, altiumBlockParser_1.parseBlocks)(contents, ['SheetNumberManager']);
    const result = {
        DesignatorManager: [],
    };
    let foundDesignatorManager = false;
    for (const block of blocks) {
        switch (block.blockName) {
            case 'DesignatorManager':
                foundDesignatorManager = true;
                result.DesignatorManager = parseAnnotationsFromBlock(block.blockContents);
                break;
            case 'SheetNumberManager':
                if (result.SheetNumberManager !== undefined) {
                    result.SheetNumberManager.SheetNumbers = [];
                    result.SheetNumberManager.Diagnostics.push('Multiple SheetNumberManager blocks are present');
                }
                else {
                    result.SheetNumberManager = parseSheetNumberManager(block.blockContents, block.duplicateKeys ?? []);
                }
                break;
        }
    }
    if (!foundDesignatorManager && contents.trim() !== '') {
        throw new Error('Expected [DesignatorManager] section in annotation file');
    }
    return result;
}
async function validateAltiumAnnotationFile(filename, fileContents) {
    if (!filename.toLowerCase().endsWith(altiumFileTypes_1.AltiumFileType.Annotation.toLowerCase())) {
        return undefined;
    }
    const textContents = await fileContents.text();
    const parsed = parseAltiumAnnotationFile(textContents);
    return {
        referencedFiles: [],
        metadata: {
            [altiumValidation_1.AltiumProjectMetadataKey.FileType]: 'Altium Annotation',
            [altiumValidation_1.AltiumProjectMetadataKey.Annotation]: parsed,
        },
    };
}
//# sourceMappingURL=altiumAnnotation.js.map